import {
  selectAreCustomizationsReady,
  selectCurrentTeam,
  selectInvoiceLimit,
  selectIsActionsBlocked,
  selectMerchantCustomizations,
  selectUserDetails,
  showNotifier
} from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCreateInvoiceMutation,
  useLazyGetCustomerByIdQuery,
  useParseInvoiceMutation,
  useSubmitAutoFillFeedbackMutation
} from '../api';
import { PreviewBar } from '../components';
import { AutoFillAttachment } from 'components/auto-fill-generic';
import { RISK_VALIDATION_ENTITY, RiskValidationErrorResponse } from '../invoices.model';
import {
  addInvoiceValidationError,
  enableCustomerNotSelectedError,
  initialiseInvoicePaymentModes,
  resetAutoFilledData,
  resetInvoiceState,
  selectAppliedLineItemLevelTaxes,
  selectAutoFillKey,
  selectHasEstimateRelatedJob,
  selectInvoice,
  selectInvoiceAutoFilledData,
  selectInvoiceDate,
  selectInvoiceFinalAmount,
  selectInvoiceFromEstimateNumber,
  selectInvoiceFromEstimateStatus,
  selectInvoiceFromJobNumber,
  selectInvoicePaymentModes,
  selectInvoiceReferenceNumber,
  selectIsInitialisingSliceData,
  selectIsInvoiceFromEstimate,
  selectIsInvoiceFromJob,
  selectIsSignatureEnabled,
  selectPartialPaymentEnabled,
  updateDueDate,
  updateInvoiceCreatedResponse,
  updateInvoiceCustomer,
  updateInvoiceDefaultsFromSetting,
  updateInvoiceFeeSettingsFromCustomizations,
  updateInvoiceReferenceNumber,
  updateIsSignatureEnabled,
  updatePartialPaymentEnabled,
  updatePaymentReminders,
  updatePaymentRemindersEnabled
} from '../InvoicesSlice';
import InvoiceOptionsSection from './components/InvoiceOptionsSection';
import InvoicesTabSideNav from './components/InvoicesTabSideNav';
import LineItemsSection from './components/LineItemsSection';
import NotesAttachmentsSection from './components/NotesAttachmentsSection';
import PaymentOptionsSection from './components/PaymentOptionsSection';
import InvoiceFormRemindersSection from './components/InvoiceFormRemindersSection';
import { FIREBASE_FIELD, INVOICE_AUTO_FILL_FEATURES, InvoiceTabEnum, minimumLineItemsError } from './constants';

import routesPath from 'routes/RoutesPath';
import { InvoicesRouteState } from 'routes/types';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotification } from 'utils/Notifier';
import queryString from 'query-string';
import { parseToFixedTwoDecimals, throttle } from 'utils/commonUtils';
import { ErrorResponse } from 'containers/app/app.model';
import { logAnalyticEvent } from 'utils/analytics';
import { RiskControlActionType, webInvoiceCreateRiskControl } from '../events';
import { FirebaseStorage } from 'services/firebase';
import {
  checkIfAnyPaymentModeIsEligible,
  checkIfInvoiceTotalExceedsPaymentModeLimit,
  doesServiceChargeNameContainTax,
  getInvoiceThresholdValidationErrors,
  getIsSignatureEnabledState,
  hasChangesToSync,
  recalculateReminderDates
} from '../utils';
import { DispatchDestination, ENTITY_TYPE, invoiceMinimumAmount } from 'constants/common';
import { useGetItemSuggestionsAndGlobalTaxesAndFeesQuery } from 'containers/product-catalog/api';
import MoreOptionsSection from './components/MoreOptionsSection';
import { FeatureToggleState, PartialPaymentOption } from 'containers/settings/constants';
import { useLazyGetInvoiceSettingsQuery } from 'containers/settings/invoice-settings/api';
import { transformSettingApiToComponentReminders } from 'containers/settings/utils';
import { useLazyGetJobByIdQuery } from 'containers/jobs/api';
import CreateInvoiceCustomer from './components/CreateInvoiceCustomer';
import { GenericModal, Loader } from 'components';
import AutoFillFeatureBanner from './components/AutoFillFeatureBanner';
import InvoiceAutoFillModal from './components/InvoiceAutoFillModal';
import { AutoFillFeedbackBanner, AutoFillFeedbackModal } from 'components/auto-fill-generic';
import AutoFillFeatureInfoModal from './components/AutoFillFeatureInfoModal';
import { CrossIconvector } from 'assets/icons';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import InvoiceRiskWarningsPopup from '../components/InvoiceRiskWarningsPopup';
import InvoiceTaxEncouragedPopup from '../components/InvoiceTaxEncouragedPopup';
import AutofillAmountMismatchPopup from '../components/AutofillAmountMismatchPopup';
import InvoiceLimitExceededPopup from '../components/InvoiceLimitExceededPopup';
import PartialPaymentAvailablePopup from '../components/PartialPaymentAvailablePopup';
import { InvoiceErrorFieldsDefault } from './types';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { useLazyGetEstimateByIdQuery } from 'containers/estimates/api';
import { EstimateStatus } from 'containers/estimates/constants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';
import { getCreateInvoiceV2Payload, getDueDate } from './util';
import { RelatedEntityData } from './types';
import DateUtils from 'utils/dateUtils';
import { buildPaymentContext } from 'utils/payment-context';
import { selectAutoFilledDataCount } from '../InvoicesSlice';
import { InvoiceSourceEnum } from '../constants';
import { useSyncFeeSettingsFromCustomizations } from '../hooks/useSyncFeeSettingsFromCustomizations';

interface ICreateInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

interface CreateInvoiceProps {
  onPreviewClick: () => void;
}

export const CreateInvoiceContext = createContext<ICreateInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

const CreateInvoice: FC<CreateInvoiceProps> = ({ onPreviewClick }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentUser = useAppSelector(selectUserDetails);
  const [parseInvoice, { isLoading: isParseInvoiceLoading, isSuccess: isParseInvoiceSuccess }] =
    useParseInvoiceMutation();
  const [submitAutoFillFeedback, { isLoading: isSubmitInvoiceFeedbackLoading }] = useSubmitAutoFillFeedbackMutation();
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const selectedInvoice = useAppSelector(selectInvoice);
  const selectedIsSignatureEnabled = useAppSelector(selectIsSignatureEnabled);
  const invoiceReferenceNumber = useAppSelector(selectInvoiceReferenceNumber);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJob);
  const invoiceFromJobNumber = useAppSelector(selectInvoiceFromJobNumber);
  const invoiceFromEstimateNumber = useAppSelector(selectInvoiceFromEstimateNumber);
  const isInitialising = useAppSelector(selectIsInitialisingSliceData);
  const customizationsReady = useAppSelector(selectAreCustomizationsReady);
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);
  const isInvoiceFromEstimate = useAppSelector(selectIsInvoiceFromEstimate);
  const estimateStatus = useAppSelector(selectInvoiceFromEstimateStatus);
  const hasEstimateRelatedJob = useAppSelector(selectHasEstimateRelatedJob);
  const isEstimatePending = useMemo(() => estimateStatus?.toUpperCase() === EstimateStatus.PENDING, [estimateStatus]);
  const invoiceDateFromState = useAppSelector(selectInvoiceDate);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabled);
  const invoicePaymentModes = useAppSelector(selectInvoicePaymentModes);
  const invoiceTotal = useAppSelector(selectInvoiceFinalAmount);
  const invoiceLimits = useAppSelector(selectInvoiceLimit);
  const appliedLineItemLevelTaxes = useAppSelector(selectAppliedLineItemLevelTaxes);
  const autoFillKey = useAppSelector(selectAutoFillKey);
  const autoFilledDataCount = useAppSelector(selectAutoFilledDataCount);
  const autoFilledData = useAppSelector(selectInvoiceAutoFilledData);

  const invoiceDate = useMemo(
    () => (invoiceDateFromState ? DateUtils.parseISODateString(invoiceDateFromState) : DateUtils.getNow()),
    [invoiceDateFromState]
  );
  //mandatory data for the invoice
  const hasUserAndLineItemsData = useMemo(
    () => selectedInvoice.invoiceCustomer !== null || selectedInvoice.invoiceLineItems.length > 0,
    [selectedInvoice.invoiceCustomer, selectedInvoice.invoiceLineItems.length]
  );

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showMerchantBlockedPopup, setShowMerchantBlockedPopup] = useState(false);
  const [showPartialPaymentAvailablePopup, setShowPartialPaymentAvailablePopup] = useState(false);
  const [showInvoiceLimitExceededPopup, setShowInvoiceLimitExceededPopup] = useState(false);
  const [showTaxEncouragedPopup, setShowTaxEncouragedPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showInvoiceAutoFillModal, setShowInvoiceAutoFillModal] = useState(false);
  const [showAutoFillFeatureInfoModal, setShowAutoFillFeatureInfoModal] = useState(false);
  const [showAutoFillFeedbackModal, setShowAutoFillFeedbackModal] = useState(false);
  const [showAutoFillFeedbackBanner, setShowAutoFillFeedbackBanner] = useState(false);
  const [hasSeenAutoFillInfo, setHasSeenAutoFillInfo] = useState(false);
  const [showAutoFillBanner, setShowAutoFillBanner] = useState(false);
  const [showAutofillAmountMismatchPopup, setShowAutofillAmountMismatchPopup] = useState(false);
  const prevParseSuccessRef = useRef(false);

  useGetItemSuggestionsAndGlobalTaxesAndFeesQuery({
    teamId: currentTeam.id,
    profession: currentTeam.profession,
    dispatchDestination: DispatchDestination.INVOICE_SLICE
  });

  useSyncFeeSettingsFromCustomizations(updateInvoiceFeeSettingsFromCustomizations, {
    isReady: !isInitialising,
    teamId: currentTeam?.id
  });
  const invoiceAutofillEnabled = merchantCustomisations?.teamFeatures?.invoiceAutofillEnabled;
  const [getInvoiceSettings] = useLazyGetInvoiceSettingsQuery();

  // Read auto-fill related flags from Firebase on mount
  useEffect(() => {
    const loadAutoFillFlags = async () => {
      if (currentUser?.id) {
        const hasSeenInfoFieldName = FIREBASE_FIELD.INVOICE_AUTOFILL_MODAL;
        const hasSeenBannerFieldName = FIREBASE_FIELD.INVOICE_AUTOFILL_BANNER;

        // Load both flags in parallel
        const [hasSeenInfo, hasSeenBanner] = await Promise.all([
          FirebaseStorage.getMerchantCollectionFieldValue({
            merchantId: currentUser.id,
            collectionFieldName: hasSeenInfoFieldName
          }),
          FirebaseStorage.getMerchantCollectionFieldValue({
            merchantId: currentUser.id,
            collectionFieldName: hasSeenBannerFieldName
          })
        ]);

        setHasSeenAutoFillInfo(hasSeenInfo);
        setShowAutoFillBanner(!hasSeenBanner);
      }
    };
    loadAutoFillFlags();
  }, [currentUser?.id]);

  useEffect(() => {
    if (customizationsReady && !hasUserAndLineItemsData) {
      dispatch(updateIsSignatureEnabled(getIsSignatureEnabledState(merchantCustomisations.customerSignatureConfig)));
      dispatch(updateCustomizations(merchantCustomisations));
      dispatch(initialiseInvoicePaymentModes(merchantCustomisations.paymentModes));
    }
  }, [customizationsReady, hasUserAndLineItemsData, merchantCustomisations, dispatch]);

  // Fetch invoice settings on mount
  useEffect(() => {
    if (currentTeam.id) {
      getInvoiceSettings({ teamId: currentTeam.id }).then(response => {
        if (response.data) {
          const defaultReminders = transformSettingApiToComponentReminders(response.data.reminders || [], false);
          const settingsRemindersEnabled = response.data.remindersEnabled || false;
          const isPartialPaymentEnabled = response.data.partialPaymentStatus !== PartialPaymentOption.OFF_BY_DEFAULT;

          // Store default reminders and settings config in Redux
          dispatch(
            updateInvoiceDefaultsFromSetting({
              reminders: defaultReminders,
              remindersEnabled: settingsRemindersEnabled,
              partialPayment: response.data.partialPaymentStatus,
              dueDateDuration: response.data.dueDate?.duration
            })
          );

          if (!hasUserAndLineItemsData) {
            const dueDateDuration = response.data.dueDate?.duration;
            const dueDateISO = getDueDate(invoiceDate, dueDateDuration)?.toISOString();
            dispatch(updateDueDate(dueDateISO));

            const updatedReminders = recalculateReminderDates(defaultReminders, dueDateISO);
            dispatch(updatePaymentReminders(updatedReminders));

            if (settingsRemindersEnabled) {
              dispatch(updatePaymentRemindersEnabled(settingsRemindersEnabled));
            }
            dispatch(updatePartialPaymentEnabled(isPartialPaymentEnabled));
          }
        }
      });
    }
  }, [currentTeam.id, dispatch, isInvoiceFromJob, isInvoiceFromEstimate]);

  const [
    createInvoiceMutation,
    {
      isSuccess: isCreateInvoiceSuccess,
      data: invoiceCreatedResponse,
      isError: isCreateInvoiceError,
      error: createInvoiceError,
      isLoading: isCreateInvoiceLoading
    }
  ] = useCreateInvoiceMutation();

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);
  const [riskValidationErrors, setRiskValidationErrors] = useState<RiskValidationErrorResponse[]>([]);
  const [showRiskWarningsPopup, setShowRiskWarningsPopup] = useState(false);
  const [riskWarnings, setRiskWarnings] = useState<string[]>([]);
  const pendingSkipWarningsRef = useRef(false);
  const invoiceCreatedSuccessRef = useRef(false);
  const lastShownValidationErrorsRef = useRef<typeof InvoiceErrorFieldsDefault | null>(null);

  useEffect(() => {
    const state = location.state as
      | { riskValidationErrors?: RiskValidationErrorResponse[]; riskWarnings?: string[] }
      | undefined;
    if (state?.riskValidationErrors?.length) {
      setRiskValidationErrors(state.riskValidationErrors);
      setRiskWarnings(state.riskWarnings ?? []);
      setShowRiskWarningsPopup(!!state.riskWarnings?.length);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    return () => {
      if (!invoiceCreatedSuccessRef.current) {
        logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.abandoned });
      }
    };
  }, []);

  const queryParams = new URLSearchParams(window.location.search);

  const urlQuery = queryString.parse(location.search);
  const jobId = queryParams.get('jobId');
  const estimateId = queryParams.get('estimateId');
  const [getCustomerByIdQuery, getCustomerByIdState] = useLazyGetCustomerByIdQuery();
  const { data: customerData, isSuccess: customerDataSuccess } = getCustomerByIdState;
  const [getJobByIdQuery, { isFetching: isJobByIdFetching }] = useLazyGetJobByIdQuery();

  const [getEstimateByIdQuery, { isFetching: isEstimateByIdFetching }] = useLazyGetEstimateByIdQuery();

  useEffect(() => {
    if (urlQuery.customerId) {
      const { customerId } = urlQuery;
      getCustomerByIdQuery({ teamId: currentTeam.id, customerId: customerId as string });
    }
  }, []);

  useEffect(() => {
    if (jobId && customizationsReady) {
      getJobByIdQuery({ teamId: currentTeam.id, jobId: jobId, isFromInvoice: true });
    }
  }, [jobId, currentTeam.id, customizationsReady]);

  useEffect(() => {
    if (estimateId && customizationsReady) {
      getEstimateByIdQuery({ teamId: currentTeam.id, estimateId, isFromInvoice: true });
    }
  }, [estimateId, currentTeam.id, customizationsReady]);

  useEffect(() => {
    if (isInvoiceFromJob && !isInitialising && !isLoading) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Customer and job details copied to invoice',
            isMessageHtml: false
          },
          showClose: true,
          type: NotifierTypes.SUCCESS
        })
      );
    }
  }, [isInitialising, isInvoiceFromJob, isLoading]);

  useEffect(() => {
    if (customizationsReady && !isInitialising && !isEstimateByIdFetching && !isJobByIdFetching) {
      setIsLoading(false);
    }
  }, [customizationsReady, isInitialising, isEstimateByIdFetching, isJobByIdFetching]);

  useEffect(() => {
    if (customerDataSuccess) {
      dispatch(updateInvoiceCustomer({ ...customerData, id: null, ID: customerData?.id }));
    }
  }, [customerDataSuccess]);

  useEffect(() => {
    if (!isCreateInvoiceSuccess || !invoiceCreatedResponse) {
      return;
    }
    const data = invoiceCreatedResponse as import('../invoices.model').CreateInvoiceWithRiskValidationResponse;
    const hasRiskErrors = data.isRiskValidationFailed && (data.riskValidations?.errors?.length ?? 0) > 0;
    const hasWarnings = (data.riskValidations?.warnings?.length ?? 0) > 0;
    setShowSyncConfirmationPopup(false);
    if (pendingSkipWarningsRef.current) {
      pendingSkipWarningsRef.current = false;
      setRiskValidationErrors([]);
      setShowRiskWarningsPopup(false);
      setRiskWarnings([]);
      dispatch(resetInvoiceState());
      if (autoFillKey) {
        dispatch(resetAutoFilledData());
      }
      dispatch(updateInvoiceCreatedResponse(data));
      setShowSyncConfirmationPopup(false);
      invoiceCreatedSuccessRef.current = true;
      logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.success });
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: location.state.returnToState || null,
          replace: true
        });
        showNotification(dispatch, NotifierTypes.SUCCESS, 'Invoice created successfully!');
      } else {
        const invoiceRouteState: InvoicesRouteState = {
          showShareModal: true,
          isEditInvoiceShare: false,
          showSidePanel: false
        };
        navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
      }
      return;
    }

    if (hasRiskErrors) {
      setRiskValidationErrors(data.riskValidations.errors);
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_risk_errors_blocked,
        BackendErrorEntities: data.riskValidations.errors.map(e => e.entity).join(',')
      });
      return;
    }

    if (hasWarnings) {
      setShowRiskWarningsPopup(true);
      setRiskWarnings(data.riskValidations.warnings);
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_warning_shown,
        BackendWarningCount: data.riskValidations.warnings.length
      });
      return;
    }

    setRiskValidationErrors([]);
    setShowRiskWarningsPopup(false);
    setRiskWarnings([]);
    dispatch(resetInvoiceState());
    if (autoFillKey) {
      dispatch(resetAutoFilledData());
    }
    dispatch(updateInvoiceCreatedResponse(data));
    invoiceCreatedSuccessRef.current = true;
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.success });
    if (location.state?.returnTo) {
      navigate(location.state.returnTo, {
        state: location.state.returnToState || null,
        replace: true
      });
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Invoice created successfully!',
            secondaryMessage: '',
            isMessageHtml: false
          },
          type: NotifierTypes.SUCCESS,
          showClose: true
        })
      );
    } else {
      const invoiceRouteState: InvoicesRouteState = {
        showShareModal: true,
        isEditInvoiceShare: false,
        showSidePanel: false
      };
      navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
    }
  }, [isCreateInvoiceSuccess, invoiceCreatedResponse]);

  useEffect(() => {
    if (isCreateInvoiceError) {
      const error = createInvoiceError as ErrorResponse;
      const backendErrorDetail =
        typeof error?.data?.details === 'string' ? error.data.details : error?.data?.message ?? undefined;
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_error,
        ...(backendErrorDetail && { BackendErrorDetail: backendErrorDetail })
      });
      if (error?.data?.details) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.details,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Something went wrong',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isCreateInvoiceError]);

  const handleScrollToTab = (invoiceTab: InvoiceTabEnum) => {
    const tabElem = document.getElementById(invoiceTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  useEffect(() => {
    handleScrollToTab(selectedInvoiceTab);
  }, [selectedInvoiceTab]);

  const onPreviewButtonClick = () => {
    let isPageIncomplete = false;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      dispatch(addInvoiceValidationError(minimumLineItemsError));
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      dispatch(enableCustomerNotSelectedError());
    }

    if (isPageIncomplete) {
      return;
    }

    onPreviewClick();
  };

  const hasDataChanges = useMemo(() => {
    return hasChangesToSync(
      selectedInvoice.invoiceCustomer,
      selectedInvoice.initialInvoiceCustomer,
      selectedInvoice.invoiceLineItems,
      selectedInvoice.initialInvoiceLineItems,
      selectedInvoice.selectedServiceCharge,
      selectedInvoice.initialServiceCharge,
      selectedInvoice.invoiceSubTotal,
      selectedInvoice.initialInvoiceSubTotal,
      selectedInvoice.isServiceFeeEnabled,
      selectedInvoice.isServiceFeeEnabledInitial,
      selectedInvoice.currentSurcharge,
      selectedInvoice.initialSurcharge
    );
  }, [
    selectedInvoice.invoiceCustomer,
    selectedInvoice.initialInvoiceCustomer,
    selectedInvoice.invoiceLineItems,
    selectedInvoice.initialInvoiceLineItems,
    selectedInvoice.selectedServiceCharge,
    selectedInvoice.initialServiceCharge,
    selectedInvoice.invoiceSubTotal,
    selectedInvoice.initialInvoiceSubTotal,
    selectedInvoice.isServiceFeeEnabled,
    selectedInvoice.isServiceFeeEnabledInitial,
    selectedInvoice.currentSurcharge,
    selectedInvoice.initialSurcharge
  ]);

  const handleCreateInvoiceV8 = useCallback(
    async (entityData?: RelatedEntityData, skipWarnings?: boolean) => {
      const v2Payload = getCreateInvoiceV2Payload(
        selectedInvoice,
        currentTeam,
        merchantCustomisations?.tnc,
        merchantCustomisations,
        entityData
      );
      const creationContext = await buildPaymentContext();
      const payload = {
        ...v2Payload,
        ...(Object.keys(creationContext).length ? { creationContext } : {}),
        ...(skipWarnings ? { skipWarnings: true } : {})
      };
      if (skipWarnings) {
        pendingSkipWarningsRef.current = true;
      }
      createInvoiceMutation({ teamId: currentTeam.id.toString(), payload });
    },
    [selectedInvoice, currentTeam, merchantCustomisations, createInvoiceMutation]
  );

  const handleCreateInvoiceFromOtherEntities = useCallback(
    (skipWarnings?: boolean) => {
      let entityData: RelatedEntityData | undefined;

      if (isInvoiceFromEstimate) {
        entityData = { entityId: estimateId, entityType: 'ESTIMATE', entityNumber: invoiceFromEstimateNumber };
      } else if (isInvoiceFromJob) {
        entityData = { entityId: jobId, entityType: 'JOB', entityNumber: invoiceFromJobNumber };
      }

      handleCreateInvoiceV8(entityData, skipWarnings);
    },
    [
      isInvoiceFromEstimate,
      estimateId,
      invoiceFromEstimateNumber,
      isInvoiceFromJob,
      jobId,
      invoiceFromJobNumber,
      handleCreateInvoiceV8
    ]
  );

  const customerRiskError = useMemo(() => {
    const customerErrors = riskValidationErrors.filter(
      e => e.entity?.toLowerCase() === RISK_VALIDATION_ENTITY.CUSTOMER
    );
    return customerErrors.length > 0 ? customerErrors.map(e => e.message).join(' ') : undefined;
  }, [riskValidationErrors]);

  const lineItemRiskError = useMemo(() => {
    const lineItemErrors = riskValidationErrors.filter(
      e => e.entity?.toLowerCase() === RISK_VALIDATION_ENTITY.LINE_ITEM
    );
    return lineItemErrors.length > 0 ? lineItemErrors.map(e => e.message).join(' ') : undefined;
  }, [riskValidationErrors]);

  useEffect(() => {
    if (customerRiskError) {
      handleScrollToTab(InvoiceTabEnum.BILL_TO);
      return;
    }
    if (lineItemRiskError) {
      handleScrollToTab(InvoiceTabEnum.LINE_ITEMS);
    }
  }, [customerRiskError, lineItemRiskError]);

  const handleClearCustomerRiskError = useCallback(() => {
    setRiskValidationErrors(prev => prev.filter(e => e.entity?.toLowerCase() !== RISK_VALIDATION_ENTITY.CUSTOMER));
  }, []);

  const handleClearLineItemRiskError = useCallback(() => {
    setRiskValidationErrors(prev => prev.filter(e => e.entity?.toLowerCase() !== RISK_VALIDATION_ENTITY.LINE_ITEM));
  }, []);

  const handleRiskWarningsCreateAnyway = useCallback(() => {
    setShowRiskWarningsPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.backend_warning_confirmed });
    if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities(true);
    } else {
      handleCreateInvoiceV8(undefined, true);
    }
  }, [isInvoiceFromEstimate, isInvoiceFromJob, handleCreateInvoiceFromOtherEntities, handleCreateInvoiceV8]);

  const handleRiskWarningsGotIt = useCallback(() => {
    setShowRiskWarningsPopup(false);
    setRiskWarnings([]);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.backend_warning_closed });
  }, []);

  const onCreateAndShare = useCallback(() => {
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.attempted });
    if (isActionsBlocked) {
      setShowMerchantBlockedPopup(true);
      return;
    }
    const effectiveMaxLimit = partialPaymentEnabled
      ? +invoiceLimits.invoiceMaxLimit
      : +merchantCustomisations?.maximumAmount || 0;
    if (effectiveMaxLimit > 0 && selectedInvoice.finalAmount > effectiveMaxLimit) {
      const errors = { ...InvoiceErrorFieldsDefault, totalAmount: true };
      lastShownValidationErrorsRef.current = errors;
      setShowInvoiceErrorsPopup({ open: true, errors });
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.frontend_validation_failed,
        ValidationFailed: Object.entries(errors)
          .filter(([, v]) => v)
          .map(([k]) => k)
          .join(',')
      });
      return;
    }
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    const invoiceMinAmount = +merchantCustomisations?.minimumAmount || invoiceMinimumAmount;
    if (selectedInvoice.finalAmount < invoiceMinAmount) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModes.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;

    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        merchantCustomisations?.paymentModes,
        selectedInvoice.finalAmount,
        partialPaymentEnabled
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    const partialPaymentRiskLimit = merchantCustomisations?.invoiceLimits?.partialPaymentRiskThresholdLimit ?? '';
    const usePartialPaymentRiskLimit =
      partialPaymentEnabled && partialPaymentRiskLimit !== '' && +partialPaymentRiskLimit > 0;
    const effectiveThreshold = usePartialPaymentRiskLimit
      ? partialPaymentRiskLimit
      : merchantCustomisations?.invoiceLimits?.invoiceValidationThreshold || '';

    const thresholdErrors = getInvoiceThresholdValidationErrors({
      totalAmount: selectedInvoice.finalAmount,
      threshold: effectiveThreshold,
      customer: selectedInvoice.invoiceCustomer
    });
    if (Object.keys(thresholdErrors)?.length > 0) {
      isPageIncomplete = true;
      errors = { ...errors, ...thresholdErrors };
    }
    const taxMandatoryThreshold = +merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold || 0;
    const taxEncouragedThreshold = +merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold || 0;
    const hasAtLeastOneTax = appliedLineItemLevelTaxes.length > 0;
    const hasTaxSatisfied = hasAtLeastOneTax || doesServiceChargeNameContainTax(selectedInvoice.selectedServiceCharge);
    if (taxMandatoryThreshold > 0 && selectedInvoice.finalAmount >= taxMandatoryThreshold && !hasTaxSatisfied) {
      isPageIncomplete = true;
      errors = { ...errors, taxRequiredAboveMandatory: true };
    }
    const isAboveEncouragedBelowMandatoryNoTax =
      taxEncouragedThreshold > 0 &&
      selectedInvoice.finalAmount >= taxEncouragedThreshold &&
      (taxMandatoryThreshold === 0 || selectedInvoice.finalAmount < taxMandatoryThreshold) &&
      !hasTaxSatisfied;
    if (isAboveEncouragedBelowMandatoryNoTax && isPageIncomplete) {
      errors = { ...errors, taxEncouragedWarning: true };
    }

    if (isPageIncomplete) {
      lastShownValidationErrorsRef.current = errors;
      const validationFailedKeys = Object.entries(errors)
        .filter(([, v]) => v)
        .map(([k]) => k);
      setShowInvoiceErrorsPopup({ open: true, errors });
      if (errors.taxRequiredAboveMandatory) {
        logAnalyticEvent(webInvoiceCreateRiskControl, {
          ActionType: RiskControlActionType.frontend_validation_tax_mandatory,
          ValidationFailed: validationFailedKeys.join(',')
        });
      } else {
        logAnalyticEvent(webInvoiceCreateRiskControl, {
          ActionType: RiskControlActionType.frontend_validation_failed,
          ValidationFailed: validationFailedKeys.join(',')
        });
      }
      return;
    }

    if (isAboveEncouragedBelowMandatoryNoTax) {
      setShowTaxEncouragedPopup(true);
      logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_shown });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    // Check if partial payment is enabled but invoice total exceeds invoice limit
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }

    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }

    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges;

    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoiceV8();
    }
  }, [
    isActionsBlocked,
    selectedInvoice.invoiceLineItems.length,
    selectedInvoice.invoiceCustomer,
    selectedInvoice.finalAmount,
    merchantCustomisations?.minimumAmount,
    merchantCustomisations?.maximumAmount,
    merchantCustomisations?.invoiceLimits?.invoiceValidationThreshold,
    merchantCustomisations?.invoiceLimits?.partialPaymentRiskThresholdLimit,
    merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold,
    merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold,
    partialPaymentEnabled,
    selectedInvoice.invoicePaymentModes,
    merchantCustomisations?.paymentModes,
    appliedLineItemLevelTaxes.length,
    selectedInvoice.selectedServiceCharge?.name,
    invoiceTotal,
    invoiceLimits.invoiceMaxLimit,
    invoicePaymentModes,
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isEstimatePending,
    hasDataChanges,
    selectedInvoice.invoiceCustomer,
    selectedInvoice.selectedServiceCharge,
    handleCreateInvoiceV8,
    handleCreateInvoiceFromOtherEntities
  ]);

  const handleOnCreateInvoiceWithoutSelfCheckout = useCallback(() => {
    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges;
    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoiceV8();
    }
  }, [
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isEstimatePending,
    hasDataChanges,
    handleCreateInvoiceV8,
    handleCreateInvoiceFromOtherEntities
  ]);

  const handleOnEditPaymentMethods = useCallback(() => {
    handleCloseNoSelfCheckoutPopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  }, []);

  const handleCloseNoSelfCheckoutPopup = useCallback(() => {
    setShowNoSelfCheckoutPopup(false);
  }, []);

  const handleCloseInvoiceErrorsPopup = useCallback(() => {
    const lastErrors = lastShownValidationErrorsRef.current;
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
    if (lastErrors) {
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.frontend_validation_dialog_closed,
        ValidationFailed: Object.entries(lastErrors)
          .filter(([, v]) => v)
          .map(([k]) => k)
          .join(',')
      });
      lastShownValidationErrorsRef.current = null;
    }
  }, []);

  const handleProceedFromTaxEncouraged = useCallback(() => {
    setShowTaxEncouragedPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_continued });
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModes.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }
    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }
    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges;
    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoiceV8();
    }
  }, [
    selectedInvoice.invoicePaymentModes,
    partialPaymentEnabled,
    invoiceTotal,
    invoiceLimits.invoiceMaxLimit,
    invoicePaymentModes,
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isEstimatePending,
    hasDataChanges,
    handleCreateInvoiceFromOtherEntities,
    handleCreateInvoiceV8
  ]);

  const handleCloseTaxEncouragedPopup = useCallback(() => {
    setShowTaxEncouragedPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_closed });
  }, []);

  const handleAddTaxFromTaxEncouraged = useCallback(() => {
    setShowTaxEncouragedPopup(false);
    setSelectedInvoiceTab(InvoiceTabEnum.LINE_ITEMS);
    handleScrollToTab(InvoiceTabEnum.LINE_ITEMS);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_add_tax });
  }, []);

  const handleClosePartialPaymentAvailablePopup = useCallback(() => {
    setShowPartialPaymentAvailablePopup(false);
  }, []);

  const handleCreateAnyway = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges;
    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoiceV8();
    }
  }, [
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isEstimatePending,
    hasDataChanges,
    handleCreateInvoiceFromOtherEntities,
    handleCreateInvoiceV8
  ]);

  const handleEditInvoiceFromPartialPaymentPopup = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  }, []);

  const handleCloseInvoiceLimitExceededPopup = useCallback(() => {
    setShowInvoiceLimitExceededPopup(false);
  }, []);

  const handleEditInvoiceFromLimitExceededPopup = useCallback(() => {
    handleCloseInvoiceLimitExceededPopup();
  }, []);

  const handleAutoFillSave = useCallback(
    async (content: string, attachments: AutoFillAttachment[]) => {
      const timeZone = DateUtils.getTimezoneLongName();
      await parseInvoice({
        teamId: currentTeam.id.toString(),
        body: { content, timeZone, attachments, source: InvoiceSourceEnum.WEB }
      }).unwrap();
    },
    [parseInvoice, currentTeam.id]
  );

  useEffect(() => {
    const handleAutoFillSuccess = async () => {
      if (isParseInvoiceSuccess) {
        setShowInvoiceAutoFillModal(false);
        setShowAutoFillFeedbackBanner(true);

        const justBecameSuccess = !prevParseSuccessRef.current;
        prevParseSuccessRef.current = true;

        const amountMismatch = autoFilledData?.amountMismatch;
        if (justBecameSuccess && amountMismatch?.showAmountMismatchPopup === true) {
          const autofillRequestAmount = amountMismatch.autofillRequestAmount;
          if (
            autofillRequestAmount != null &&
            autofillRequestAmount !== '' &&
            parseToFixedTwoDecimals(invoiceTotal) !== parseToFixedTwoDecimals(autofillRequestAmount)
          ) {
            setShowAutofillAmountMismatchPopup(true);
          }
        }

        // Mark banner as seen in Firebase - only if not already marked
        if (currentUser?.id && showAutoFillBanner) {
          const collectionFieldName = FIREBASE_FIELD.INVOICE_AUTOFILL_BANNER;
          await FirebaseStorage.updateMerchantCollectionFieldValue({
            merchantId: currentUser.id,
            collectionFieldName,
            value: true
          });
          setShowAutoFillBanner(false);
        }
      } else {
        prevParseSuccessRef.current = false;
      }
    };

    handleAutoFillSuccess();
  }, [isParseInvoiceSuccess, currentUser?.id, showAutoFillBanner, invoiceTotal, autoFilledData]);

  const thankYouToast = useCallback(() => {
    showNotification(
      dispatch,
      NotifierTypes.SUCCESS,
      'Thank you for your feedback.',
      4000,
      'autofill-feedback-success'
    );
  }, [dispatch]);

  const handleFeedbackLike = useCallback(async () => {
    if (isSubmitInvoiceFeedbackLoading) {
      return;
    }
    try {
      await submitAutoFillFeedback({
        teamId: currentTeam.id.toString(),
        body: {
          autofillKey: autoFillKey,
          isMerchantApproved: true,
          entityType: ENTITY_TYPE.INVOICE
        }
      }).unwrap();

      thankYouToast();
      setShowAutoFillFeedbackBanner(false);
    } catch (error) {
      showNotification(dispatch, NotifierTypes.ERROR, 'Failed to submit feedback. Please try again.');
    }
  }, [submitAutoFillFeedback, currentTeam.id, autoFillKey, dispatch]);

  const handleFeedbackDislike = useCallback(() => {
    setShowAutoFillFeedbackModal(true);
  }, []);

  const handleSendFeedback = useCallback(
    async (reason: string, comment: string) => {
      try {
        await submitAutoFillFeedback({
          teamId: currentTeam.id.toString(),
          body: {
            autofillKey: autoFillKey,
            isMerchantApproved: false,
            merchantRejectedReason: reason,
            merchantRejectedComment: comment,
            entityType: ENTITY_TYPE.INVOICE
          }
        }).unwrap();

        thankYouToast();
        setShowAutoFillFeedbackModal(false);
        setShowAutoFillFeedbackBanner(false);
      } catch (error) {
        showNotification(dispatch, NotifierTypes.ERROR, 'Failed to submit feedback. Please try again.');
      }
    },
    [submitAutoFillFeedback, currentTeam.id, autoFillKey, dispatch]
  );

  const handleTryAutoFill = useCallback(() => {
    if (!hasSeenAutoFillInfo) {
      setShowAutoFillFeatureInfoModal(true);
    } else {
      setShowInvoiceAutoFillModal(true);
    }
  }, [hasSeenAutoFillInfo]);

  const handleAutoFillFeatureInfoGotIt = useCallback(async () => {
    setHasSeenAutoFillInfo(true);
    setShowAutoFillFeatureInfoModal(false);
    setShowInvoiceAutoFillModal(true);

    // Persist to Firebase
    if (currentUser?.id) {
      const collectionFieldName = FIREBASE_FIELD.INVOICE_AUTOFILL_MODAL;
      await FirebaseStorage.updateMerchantCollectionFieldValue({
        merchantId: currentUser.id,
        collectionFieldName,
        value: true
      });
    }
  }, [currentUser?.id, currentTeam?.id]);

  const createAndShareHandler = useCallback(
    throttle(() => {
      onCreateAndShare();
    }, 800),
    [onCreateAndShare]
  );

  const onBackIconClick = () => {
    navigate(-1);
    dispatch(resetInvoiceState());
  };

  const handleSignatureStateChange = (value: boolean) => {
    dispatch(updateIsSignatureEnabled(value));
  };

  const handleInvoiceReferenceNumberChange = (value: string) => {
    dispatch(updateInvoiceReferenceNumber(value));
  };

  const checkIsSignatureDisabled = useMemo(() => {
    if (customizationsReady) {
      return merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON;
    }
    return false;
  }, [customizationsReady, merchantCustomisations?.customerSignatureConfig]);

  const syncPopupTitle = useMemo(() => {
    const showJob = isInvoiceFromJob || hasEstimateRelatedJob;
    const showEstimate = isInvoiceFromEstimate || isEstimatePending;
    const showAnd = showJob && showEstimate;

    const jobPart = showJob ? 'job' : '';
    const andPart = showAnd ? 'and' : '';
    const estimatePart = showEstimate ? 'estimate' : '';

    return `Save and sync changes to the related ${jobPart} ${andPart} ${estimatePart}?`.replace(/\s+/g, ' ').trim();
  }, [isInvoiceFromJob, hasEstimateRelatedJob, isInvoiceFromEstimate, isEstimatePending]);

  const syncPopupSubtitle = useMemo(() => {
    const showJob = isInvoiceFromJob || hasEstimateRelatedJob;
    const showEstimate = isInvoiceFromEstimate || isEstimatePending;
    const showAnd = showJob && showEstimate;

    const jobText = showJob ? `job ${invoiceFromJobNumber}` : '';
    const estimateText = showEstimate ? `estimate ${invoiceFromEstimateNumber}` : '';
    const andPart = showAnd ? ' and ' : '';
    const base = 'Changes in your invoices will be applied to the associated';
    const text = `${base} ${jobText}${andPart}${estimateText} as well.`;
    return text.replace(/\s+/g, ' ').trim();
  }, [
    isInvoiceFromJob,
    hasEstimateRelatedJob,
    invoiceFromJobNumber,
    isInvoiceFromEstimate,
    isEstimatePending,
    invoiceFromEstimateNumber
  ]);

  return (
    <CreateInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="flex h-full w-full flex-col border">
          <PreviewBar
            className="border border-borderGray"
            title="New Invoice"
            onPrimaryClick={createAndShareHandler}
            onReturn={onBackIconClick}
            primaryBtnTitle="Create & Share"
            disablePrimaryBtn={false}
            secondaryBtnTitle="Preview"
            onSecondaryClick={onPreviewButtonClick}
            primaryBtnId="create-and-share-invoice-button"
            secondaryBtnId="create-invoice-preview-button"
            onAutoFillClick={invoiceAutofillEnabled ? handleTryAutoFill : undefined}
          />
          <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
            <div
              className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11 pr-0
          lg:flex lg:w-80">
              <InvoicesTabSideNav context={CreateInvoiceContext} />
            </div>

            <div
              id="scrollable div"
              className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 pl-0">
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.BILL_TO}>
                {showAutoFillBanner && invoiceAutofillEnabled && (
                  <AutoFillFeatureBanner onTryAutoFill={handleTryAutoFill} entityType={ENTITY_TYPE.INVOICE} />
                )}
                {showAutoFillFeedbackBanner && (
                  <AutoFillFeedbackBanner
                    autoFilledFieldsCount={autoFilledDataCount}
                    onStartNewAutoFill={handleTryAutoFill}
                    onFeedbackLike={handleFeedbackLike}
                    onFeedbackDislike={handleFeedbackDislike}
                    isLoading={isSubmitInvoiceFeedbackLoading}
                  />
                )}
                <CreateInvoiceCustomer
                  context={CreateInvoiceContext}
                  customerError={customerRiskError}
                  onClearCustomerRiskError={handleClearCustomerRiskError}
                />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.INVOICE_OPTIONS}>
                <InvoiceOptionsSection context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.LINE_ITEMS}>
                <LineItemsSection
                  context={CreateInvoiceContext}
                  lineItemError={lineItemRiskError}
                  onClearLineItemRiskError={handleClearLineItemRiskError}
                />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.NOTES_ATTACHMENTS}>
                <NotesAttachmentsSection context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_OPTIONS}>
                <PaymentOptionsSection context={CreateInvoiceContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_REMINDERS}>
                <InvoiceFormRemindersSection context={CreateInvoiceContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.MORE_OPTIONS}>
                <MoreOptionsSection
                  updateSignatureState={handleSignatureStateChange}
                  signatureState={selectedIsSignatureEnabled}
                  context={CreateInvoiceContext}
                  isDisabled={checkIsSignatureDisabled}
                  id="create-invoice-allow-sign-toggle-switch"
                  invoiceReferenceNumber={invoiceReferenceNumber}
                  updateInvoiceReferenceNumber={handleInvoiceReferenceNumberChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={syncPopupTitle}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={syncPopupSubtitle}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleCreateInvoiceFromOtherEntities}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => (
            <CrossIconvector
              height={18}
              width={18}
              title="sync-popup-close"
              onClick={() => setShowSyncConfirmationPopup(false)}
              className="cursor-pointer"
            />
          )}
          isActionInProgress={isCreateInvoiceLoading}
          height="min-h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={false}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isCreateInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMaxLimit : +merchantCustomisations?.maximumAmount || 0
          }
          minimumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMinLimit : +merchantCustomisations?.minimumAmount || 0
          }
          taxEncouragedThreshold={merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold}
          taxMandatoryThreshold={merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold}
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showTaxEncouragedPopup && (
        <InvoiceTaxEncouragedPopup
          onClose={handleCloseTaxEncouragedPopup}
          onAddTax={handleAddTaxFromTaxEncouraged}
          onProceedAnyway={handleProceedFromTaxEncouraged}
          isEdit={false}
          taxEncouragedThreshold={merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold}
          isLoading={isCreateInvoiceLoading}
        />
      )}
      {showMerchantBlockedPopup && (
        <BlockedInfoPopup isOpen={showMerchantBlockedPopup} onClose={() => setShowMerchantBlockedPopup(false)} />
      )}
      {showPartialPaymentAvailablePopup && (
        <PartialPaymentAvailablePopup
          onClose={handleClosePartialPaymentAvailablePopup}
          paymentModes={invoicePaymentModes}
          onCreateAnyway={handleCreateAnyway}
          onEditInvoice={handleEditInvoiceFromPartialPaymentPopup}
        />
      )}
      {showInvoiceLimitExceededPopup && (
        <InvoiceLimitExceededPopup
          onClose={handleCloseInvoiceLimitExceededPopup}
          invoiceLimits={invoiceLimits}
          onEditInvoice={handleEditInvoiceFromLimitExceededPopup}
        />
      )}
      {showAutofillAmountMismatchPopup && (
        <AutofillAmountMismatchPopup
          onClose={() => setShowAutofillAmountMismatchPopup(false)}
          title={autoFilledData?.amountMismatch?.title}
          subTitle={autoFilledData?.amountMismatch?.subTitle}
        />
      )}
      <InvoiceRiskWarningsPopup
        open={showRiskWarningsPopup}
        warnings={riskWarnings}
        onCreateAnyway={handleRiskWarningsCreateAnyway}
        onGotIt={handleRiskWarningsGotIt}
        isEdit={false}
        isLoading={isCreateInvoiceLoading}
      />
      {showInvoiceAutoFillModal && (
        <InvoiceAutoFillModal
          isOpen={showInvoiceAutoFillModal}
          onClose={() => setShowInvoiceAutoFillModal(false)}
          onSave={handleAutoFillSave}
          isSaving={isParseInvoiceLoading}
        />
      )}
      {showAutoFillFeatureInfoModal && (
        <AutoFillFeatureInfoModal
          open={showAutoFillFeatureInfoModal}
          onClose={handleAutoFillFeatureInfoGotIt}
          onGotIt={handleAutoFillFeatureInfoGotIt}
          features={INVOICE_AUTO_FILL_FEATURES}
        />
      )}
      {showAutoFillFeedbackModal && (
        <AutoFillFeedbackModal
          isOpen={showAutoFillFeedbackModal}
          onClose={() => setShowAutoFillFeedbackModal(false)}
          onSendFeedback={handleSendFeedback}
          isLoading={isSubmitInvoiceFeedbackLoading}
        />
      )}
    </CreateInvoiceContext.Provider>
  );
};

export default CreateInvoice;
